import SKILL_MOTIONS from "../ui/skillmotion.json";
import type { ClipName } from "./creaturemesh";

/** A skill motion's clip: the motion slot a skill's `skilldesc.txt` row names
 *  (`PythonSkill.cpp:442-451`) at one of its four grades
 *  (`PythonSkill.h:141`). */
export type SkillClipName = `skill${number}g${number}`;

/** `MASTER_LEVEL`, `GRAND_MASTER_LEVEL` and `PERFECT_MASTER_LEVEL`: where
 *  `SetSkillLevel` moves a skill up a grade (`char_skill.cpp:181-189`). */
const MASTER_LEVEL = 20; // char_skill.cpp:186-187
const GRAND_MASTER_LEVEL = 30; // char_skill.cpp:184-185
const PERFECT_MASTER_LEVEL = 40; // char_skill.cpp:181-183

/** The clip `NEW_UseSkill` intercepts for a skill in `slot` at `level`:
 *  `MOTION_SKILL` plus the slot plus `SKILL_GRADEGAP` for each grade above
 *  normal (`InstanceBaseBattle.cpp:339`, `PythonSkill.cpp:506,1372-1385`), or
 *  null for a skill with no motion. */
export function skillClipFor(slot: number, level: number): SkillClipName | null {
  const grade = level >= PERFECT_MASTER_LEVEL ? 3 : level >= GRAND_MASTER_LEVEL ? 2 : level >= MASTER_LEVEL ? 1 : 0;
  return skillClipAtGrade(slot, grade);
}

/** `GetSkillMotionIndex(iGrade)`'s clip: `slot` plus `SKILL_GRADEGAP` for each
 *  of `grade`'s steps above normal (`PythonSkill.cpp:506,1372-1385`), or null
 *  for a skill with no motion. */
function skillClipAtGrade(slot: number, grade: number): SkillClipName | null {
  if (!(slot > 0)) return null;
  return `skill${slot}g${grade}`;
}

/** The clip a cast of `vnum` at `level` plays: its `skilldesc.txt` motion slot
 *  (`PythonSkill.cpp:442-451`, `tools/import_skilldesc.py`) through
 *  `skillClipFor`, or null for a skill that names none. */
export function skillMotionFor(vnum: number, level: number): SkillClipName | null {
  const slot = (SKILL_MOTIONS as Readonly<Record<string, number>>)[String(vnum)];
  return slot === undefined ? null : skillClipFor(slot, level);
}

/** How many `NAME_SPECIAL_*` motions a race carries: `MOB_SKILL_MAX_NUM` (`PythonNonPlayer.h:47`). */
const MOB_SKILL_SLOTS = 5;

/** The clip an observer draws for a body's skill move (`SkillMotionBegun`): a
 *  monster's `FUNC_MOB_SKILL` intercepts `NAME_SPECIAL_1` plus its slot
 *  (`InstanceBase.cpp:1482`), a player's `FUNC_SKILL` `NEW_UseSkill`s the cast
 *  skill's motion at its grade (`InstanceBase.cpp:1554`, `InstanceBaseBattle.cpp:339`).
 *  Null where there is no such motion. */
export function observedSkillClip(
  monster: boolean, motion: number, grade: number,
): ClipName | null {
  if (monster) return motion >= 0 && motion < MOB_SKILL_SLOTS ? `special${motion + 1}` as ClipName : null;
  const slot = (SKILL_MOTIONS as Readonly<Record<string, number>>)[String(motion)];
  return slot === undefined ? null : skillClipAtGrade(slot, grade);
}

/** A mounted motion mode, as the suffix of the rider's horse-skill clips:
 *  `MODE_HORSE` and the weapon modes `__RefreshMotionMode` selects on a horse
 *  (`InstanceBase.cpp:2896-2929`). */
export type RiderMode =
  | "Horse" | "HorseOneHand" | "HorseTwoHand" | "HorseDualHand" | "HorseBow" | "HorseFan" | "HorseBell";

/** A rider's horse-skill clip: `HORSE_SKILL_WILDATTACK`, `_CHARGE` or
 *  `_SPLASH` (`playersettingmodule.py:39-41`) as one mode registers it
 *  (`HORSE_SKILL_MODES` in `tools/build_creature_meshes.py`). */
export type RiderSkillClipName = `skill${121 | 122 | 123}g0${RiderMode}`;

const HORSE_SKILL_CLIP = /^skill12[1-3]g0$/;

/** `GetMotionKey` for a rider's horse skill `clip` in `mode`: the mode's own
 *  file, else `MODE_HORSE`'s for a mounted weapon mode, else null for no motion
 *  (`RaceData.cpp:123-150`); any other clip is itself. `has` says whether the
 *  rider's body carries a clip. */
export function riderSkillClip(clip: ClipName, mode: RiderMode, has: (clip: ClipName) => boolean): ClipName | null {
  if (!HORSE_SKILL_CLIP.test(clip)) return clip;
  const own = `${clip}${mode}` as ClipName;
  if (has(own)) return own; // RaceData.cpp:123
  const general = `${clip}Horse` as ClipName; // RaceData.cpp:129-135
  return mode !== "Horse" && has(general) ? general : null; // RaceData.cpp:144-147
}
